import type { AiUsageLog } from '@/types';
import type { SortDescriptor } from '@react-types/shared';

import React from 'react';
import { Card, CardBody, Chip, Pagination, Select, SelectItem } from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';

import DefaultLayout from '@/layouts/default';
import { PermissionGuard } from '@/components/shared/PermissionGuard';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { DomainCell } from '@/components/shared/DomainCell';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { OrganizationCell } from '@/components/shared/OrganizationCell';
import { SearchInput } from '@/components/shared/SearchInput';
import { UserCell } from '@/components/shared/UserCell';
import {
  createLoadingSkeleton,
  DataTable,
  FilterSelect,
  TABLE_CONFIG,
  TableFilters
} from '@/components/table';
import {
  AI_USAGE_FEATURE_OPTIONS,
  AI_USAGE_STATUS_OPTIONS,
  useAiUsageFilters
} from '@/hooks/ai-usage';
import {
  aiUsageAnalyticsQueryAtom,
  aiUsageListParamsAtom,
  aiUsageLogsQueryAtom
} from '@/store/aiUsage';

const LOG_COLUMNS = [
  { name: 'Time', uid: 'created_at', sortable: true },
  { name: 'Workspace', uid: 'organization' },
  { name: 'User', uid: 'user' },
  { name: 'Feature', uid: 'feature_type' },
  { name: 'Model', uid: 'model_slug' },
  { name: 'Tokens', uid: 'total_tokens', sortable: true },
  { name: 'Cost', uid: 'cost_usd', sortable: true },
  { name: 'Latency', uid: 'latency_ms', sortable: true },
  { name: 'Status', uid: 'status' },
  { name: 'Feedback', uid: 'user_feedback' }
] as const;

function getStatusColor(status: string): 'success' | 'danger' | 'warning' | 'default' {
  switch (status) {
    case 'success':
      return 'success';
    case 'error':
    case 'failed':
      return 'danger';
    case 'timeout':
      return 'warning';
    default:
      return 'default';
  }
}

function getFeedbackChip(feedback: AiUsageLog['user_feedback']) {
  if (!feedback) {
    return <span className="text-default-400 text-sm">—</span>;
  }

  if (feedback.rating === 'up') {
    return (
      <Chip color="success" size="sm" variant="flat">
        Thumbs up
      </Chip>
    );
  }

  return (
    <Chip color="danger" size="sm" variant="flat">
      Thumbs down
    </Chip>
  );
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-default-500 text-xs font-medium tracking-wide uppercase">{label}</span>
      <div className="text-default-700 text-sm">{children}</div>
    </div>
  );
}

export default function AiUsagePage() {
  const [, setListParams] = useAtom(aiUsageListParamsAtom);
  const {
    params,
    searchFields,
    setSearchFields,
    clearFilters,
    handlePageChange,
    handlePerPageChange,
    hasAnyFilter,
    sortParam,
    setSortParam
  } = useAiUsageFilters();

  const { data: analyticsData, isLoading: analyticsLoading } = useAtomValue(aiUsageAnalyticsQueryAtom);
  const { data: logsData, isLoading: logsLoading, isError: logsError, error: logsQueryError } =
    useAtomValue(aiUsageLogsQueryAtom);

  const [featureFilter, setFeatureFilter] = React.useState(searchFields.feature_type);
  const [statusFilter, setStatusFilter] = React.useState(searchFields.status);
  const [modelSlugFilter, setModelSlugFilter] = React.useState(searchFields.model_slug);
  const [organizationFilter, setOrganizationFilter] = React.useState(searchFields.organization);
  const [userEmailFilter, setUserEmailFilter] = React.useState(searchFields.user_email);
  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor>({
    column: 'created_at',
    direction: 'descending'
  });

  React.useEffect(() => {
    setFeatureFilter(searchFields.feature_type);
    setStatusFilter(searchFields.status);
    setModelSlugFilter(searchFields.model_slug);
    setOrganizationFilter(searchFields.organization);
    setUserEmailFilter(searchFields.user_email);
  }, [
    searchFields.feature_type,
    searchFields.status,
    searchFields.model_slug,
    searchFields.organization,
    searchFields.user_email
  ]);

  React.useEffect(() => {
    if (!sortParam) {
      setSortDescriptor({ column: 'created_at', direction: 'descending' });
      return;
    }

    const direction = sortParam.startsWith('-') ? 'descending' : 'ascending';
    const column = sortParam.replace(/^-/, '');
    setSortDescriptor({ column, direction });
  }, [sortParam]);

  const analytics = analyticsData?.data;
  const logs = logsData?.data ?? [];
  const meta = logsData?.meta;
  const totalPages = meta?.last_page ?? 1;
  const currentPage = meta?.current_page ?? params.page;
  const perPage = meta?.per_page ?? params.per_page;
  const totalLogs = meta?.total ?? 0;
  const showingFrom = totalLogs > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const showingTo = meta ? Math.min(currentPage * perPage, totalLogs) : 0;

  const triggerRefresh = React.useCallback(() => {
    setListParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setListParams]);

  const handleSortChange = React.useCallback(
    (descriptor: SortDescriptor) => {
      setSortDescriptor(descriptor);
      const column = descriptor.column?.toString();
      if (!column) {
        setSortParam(null);
        return;
      }
      const prefix = descriptor.direction === 'descending' ? '-' : '';
      setSortParam(`${prefix}${column}`);
    },
    [setSortParam]
  );

  const handleClearFilters = React.useCallback(() => {
    setFeatureFilter('all');
    setStatusFilter('all');
    setModelSlugFilter('');
    setOrganizationFilter('');
    setUserEmailFilter('');
    clearFilters();
  }, [clearFilters]);

  const applyTextFilters = React.useCallback(() => {
    setSearchFields((prev) => ({
      ...prev,
      model_slug: modelSlugFilter.trim(),
      organization: organizationFilter.trim(),
      user_email: userEmailFilter.trim()
    }));
  }, [modelSlugFilter, organizationFilter, userEmailFilter, setSearchFields]);

  const costByDay = React.useMemo(
    () =>
      (analytics?.by_day ?? []).map((row) => ({
        day: row.day,
        cost: Number(row.total_cost_usd ?? 0)
      })),
    [analytics]
  );

  const requestsByModel = React.useMemo(
    () =>
      (analytics?.by_model ?? []).map((row) => ({
        model: row.model_slug,
        requests: row.request_count
      })),
    [analytics]
  );

  const renderCell = React.useCallback((log: AiUsageLog, columnKey: React.Key) => {
    switch (columnKey) {
      case 'created_at':
        return <DateTimeDisplay dateString={log.created_at} showSeconds />;
      case 'organization':
        return log.organization ? (
          <OrganizationCell organization={log.organization} showDescription={false} dense linked />
        ) : (
          <span className="text-default-400 text-sm">—</span>
        );
      case 'user':
        return log.user ? (
          <UserCell
            user={{
              id: log.user.id,
              first_name: log.user.first_name ?? '',
              last_name: log.user.last_name ?? '',
              email: log.user.email
            }}
            dense
            linked
          />
        ) : (
          <span className="text-default-400 text-sm">—</span>
        );
      case 'feature_type':
        return log.feature_type_label ?? log.feature_type;
      case 'model_slug':
        return <span className="font-mono text-xs">{log.model_slug}</span>;
      case 'total_tokens':
        return (
          <div className="flex flex-col">
            <span>{log.total_tokens.toLocaleString()}</span>
            <span className="text-default-400 text-xs">
              {log.prompt_tokens.toLocaleString()} in / {log.completion_tokens.toLocaleString()} out
            </span>
          </div>
        );
      case 'cost_usd':
        return log.cost_usd ? `$${Number(log.cost_usd).toFixed(6)}` : '—';
      case 'latency_ms':
        return log.latency_ms != null ? `${log.latency_ms.toLocaleString()} ms` : '—';
      case 'status':
        return (
          <Chip color={getStatusColor(log.status)} size="sm" variant="flat">
            {log.status}
          </Chip>
        );
      case 'user_feedback':
        return getFeedbackChip(log.user_feedback);
      default:
        return null;
    }
  }, []);

  const renderCollapseContent = React.useCallback((log: AiUsageLog) => {
    return (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-content1">
            <h4 className="text-default-700 mb-4 flex items-center gap-2 text-sm font-semibold">
              <div className="bg-primary h-2 w-2 rounded-full" />
              Request Details
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailField label="Request ID">
                <span className="font-mono text-xs break-all">{log.request_id}</span>
              </DetailField>
              <DetailField label="Log ID">
                <span className="font-mono text-xs">{log.id}</span>
              </DetailField>
              <DetailField label="Feature">
                {log.feature_type_label ?? log.feature_type}
              </DetailField>
              <DetailField label="Status">
                <Chip color={getStatusColor(log.status)} size="sm" variant="flat">
                  {log.status}
                </Chip>
              </DetailField>
              <DetailField label="Model slug">
                <span className="font-mono text-xs break-all">{log.model_slug}</span>
              </DetailField>
              <DetailField label="Timestamp">
                <DateTimeDisplay dateString={log.created_at} showSeconds />
              </DetailField>
              <DetailField label="Latency">
                {log.latency_ms != null ? `${log.latency_ms.toLocaleString()} ms` : '—'}
              </DetailField>
              <DetailField label="Estimated cost">
                {log.cost_usd ? `$${Number(log.cost_usd).toFixed(8)}` : '—'}
              </DetailField>
            </div>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-content1">
            <h4 className="text-default-700 mb-4 flex items-center gap-2 text-sm font-semibold">
              <div className="bg-success h-2 w-2 rounded-full" />
              Token Usage
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <DetailField label="Prompt tokens">{log.prompt_tokens.toLocaleString()}</DetailField>
              <DetailField label="Completion tokens">{log.completion_tokens.toLocaleString()}</DetailField>
              <DetailField label="Total tokens">{log.total_tokens.toLocaleString()}</DetailField>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-content1">
            <h4 className="text-default-700 mb-4 flex items-center gap-2 text-sm font-semibold">
              <div className="bg-secondary h-2 w-2 rounded-full" />
              Context
            </h4>
            <div className="space-y-4">
              <DetailField label="Workspace">
                {log.organization ? (
                  <OrganizationCell organization={log.organization} dense linked />
                ) : (
                  <span className="text-default-400">No organization</span>
                )}
              </DetailField>
              <DetailField label="User">
                {log.user ? (
                  <UserCell
                    user={{
                      id: log.user.id,
                      first_name: log.user.first_name ?? '',
                      last_name: log.user.last_name ?? '',
                      email: log.user.email
                    }}
                    linked
                  />
                ) : (
                  <span className="text-default-400">No user</span>
                )}
              </DetailField>
              <DetailField label="Domain">
                {log.domain ? (
                  <DomainCell domain={log.domain} dense linked showID />
                ) : log.domain_id ? (
                  <span className="font-mono text-xs">Domain #{log.domain_id}</span>
                ) : (
                  <span className="text-default-400">No domain</span>
                )}
              </DetailField>
            </div>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-content1">
            <h4 className="text-default-700 mb-4 flex items-center gap-2 text-sm font-semibold">
              <div className="bg-primary h-2 w-2 rounded-full" />
              User Feedback
            </h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <DetailField label="Rating">
                {getFeedbackChip(log.user_feedback)}
              </DetailField>
              <DetailField label="Submitted at">
                {log.user_feedback?.submitted_at ? (
                  <DateTimeDisplay dateString={log.user_feedback.submitted_at} showSeconds />
                ) : (
                  <span className="text-default-400">No feedback</span>
                )}
              </DetailField>
            </div>
          </div>

          {log.ai_model ? (
            <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-content1">
              <h4 className="text-default-700 mb-4 flex items-center gap-2 text-sm font-semibold">
                <div className="bg-warning h-2 w-2 rounded-full" />
                Registered Model
              </h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailField label="Display name">{log.ai_model.display_name}</DetailField>
                <DetailField label="Provider">{log.ai_model.provider || '—'}</DetailField>
                <DetailField label="OpenRouter ID">
                  <span className="font-mono text-xs break-all">{log.ai_model.openrouter_id}</span>
                </DetailField>
                <DetailField label="Active">
                  <Chip color={log.ai_model.is_active ? 'success' : 'default'} size="sm" variant="flat">
                    {log.ai_model.is_active ? 'Active' : 'Inactive'}
                  </Chip>
                </DetailField>
                <DetailField label="Prompt pricing">
                  {log.ai_model.pricing_prompt != null ? `$${log.ai_model.pricing_prompt}/1M` : '—'}
                </DetailField>
                <DetailField label="Completion pricing">
                  {log.ai_model.pricing_completion != null ? `$${log.ai_model.pricing_completion}/1M` : '—'}
                </DetailField>
              </div>
            </div>
          ) : null}

          {log.metadata && Object.keys(log.metadata).length > 0 ? (
            <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-content1">
              <h4 className="text-default-700 mb-4 flex items-center gap-2 text-sm font-semibold">
                <div className="bg-danger h-2 w-2 rounded-full" />
                Metadata
              </h4>
              <JsonViewer data={log.metadata} maxHeightPx={240} collapsed={1} />
            </div>
          ) : null}
        </div>
      </div>
    );
  }, []);

  return (
    <DefaultLayout>
      <PermissionGuard permission="AiUsageLog.analytics">
        <div className="space-y-6 p-6">
          <div>
            <p className="text-primary text-sm font-semibold uppercase tracking-wide">Settings</p>
            <h1 className="text-3xl font-bold">AI usage & cost</h1>
            <p className="text-default-500 mt-2 text-base">Token consumption and estimated OpenRouter costs.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardBody>
                <p className="text-default-500 text-sm">Requests (30d)</p>
                <p className="text-2xl font-semibold">{analytics?.summary.request_count ?? '—'}</p>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <p className="text-default-500 text-sm">Total tokens</p>
                <p className="text-2xl font-semibold">{analytics?.summary.total_tokens?.toLocaleString() ?? '—'}</p>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <p className="text-default-500 text-sm">Estimated cost (USD)</p>
                <p className="text-2xl font-semibold">${Number(analytics?.summary.total_cost_usd ?? 0).toFixed(4)}</p>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <p className="text-default-500 text-sm">Avg latency</p>
                <p className="text-2xl font-semibold">{analytics?.summary.avg_latency_ms ?? '—'} ms</p>
              </CardBody>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardBody>
                <h2 className="mb-4 text-lg font-semibold">Cost by day</h2>
                <div style={{ height: 280 }}>
                  {!analyticsLoading && costByDay.length > 0 ? (
                    <ResponsiveLine
                      data={[
                        {
                          id: 'cost_usd',
                          data: costByDay.map((row) => ({ x: row.day, y: row.cost }))
                        }
                      ]}
                      margin={{ top: 20, right: 20, bottom: 50, left: 50 }}
                      xScale={{ type: 'point' }}
                      yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                      axisBottom={{ tickRotation: -35 }}
                      useMesh
                      enablePoints={false}
                    />
                  ) : (
                    <p className="text-default-500">No usage data yet.</p>
                  )}
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h2 className="mb-4 text-lg font-semibold">Requests by model</h2>
                <div style={{ height: 280 }}>
                  {!analyticsLoading && requestsByModel.length > 0 ? (
                    <ResponsiveBar
                      data={requestsByModel}
                      keys={['requests']}
                      indexBy="model"
                      margin={{ top: 20, right: 20, bottom: 80, left: 50 }}
                      axisBottom={{ tickRotation: -35 }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                    />
                  ) : (
                    <p className="text-default-500">No usage data yet.</p>
                  )}
                </div>
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardBody className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold">Recent usage logs</h2>
                <span className="text-default-500 text-sm">
                  {totalLogs > 0 ? `Showing ${showingFrom}–${showingTo} of ${totalLogs}` : 'No logs'}
                </span>
              </div>

              <TableFilters
                hasActiveFilters={hasAnyFilter}
                isRefreshing={logsLoading}
                onClearFilters={handleClearFilters}
                onRefresh={triggerRefresh}
              >
                <FilterSelect
                  className="w-52"
                  label="Feature"
                  options={[...AI_USAGE_FEATURE_OPTIONS]}
                  value={featureFilter}
                  onChange={(value) => {
                    const next = value as typeof featureFilter;
                    setFeatureFilter(next);
                    setSearchFields((prev) => ({ ...prev, feature_type: next }));
                  }}
                />
                <FilterSelect
                  className="w-40"
                  label="Status"
                  options={[...AI_USAGE_STATUS_OPTIONS]}
                  value={statusFilter}
                  onChange={(value) => {
                    const next = value as typeof statusFilter;
                    setStatusFilter(next);
                    setSearchFields((prev) => ({ ...prev, status: next }));
                  }}
                />
                <SearchInput
                  className="w-52"
                  placeholder="Model slug..."
                  size="sm"
                  value={modelSlugFilter}
                  onClear={() => {
                    setModelSlugFilter('');
                    setSearchFields((prev) => ({ ...prev, model_slug: '' }));
                  }}
                  onSearch={applyTextFilters}
                  onValueChange={setModelSlugFilter}
                />
                <SearchInput
                  className="w-52"
                  placeholder="Workspace..."
                  size="sm"
                  value={organizationFilter}
                  onClear={() => {
                    setOrganizationFilter('');
                    setSearchFields((prev) => ({ ...prev, organization: '' }));
                  }}
                  onSearch={applyTextFilters}
                  onValueChange={setOrganizationFilter}
                />
                <SearchInput
                  className="w-52"
                  placeholder="User email..."
                  size="sm"
                  value={userEmailFilter}
                  onClear={() => {
                    setUserEmailFilter('');
                    setSearchFields((prev) => ({ ...prev, user_email: '' }));
                  }}
                  onSearch={applyTextFilters}
                  onValueChange={setUserEmailFilter}
                />
              </TableFilters>

              <DataTable
                bottomContent={
                  <div className="flex w-full items-center justify-between gap-4 px-2 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-default-500 text-sm">Rows per page</span>
                      <Select
                        aria-label="Rows per page"
                        className="w-20"
                        selectedKeys={new Set([String(perPage)])}
                        size="sm"
                        onSelectionChange={(keys) => {
                          const value = Number(Array.from(keys)[0]);
                          if (Number.isFinite(value)) handlePerPageChange(value);
                        }}
                      >
                        {TABLE_CONFIG.pagination.perPageOptions.map((option) => (
                          <SelectItem key={String(option)} textValue={String(option)}>
                            {option}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                    {totalPages > 1 ? (
                      <Pagination
                        isCompact
                        showControls
                        color="primary"
                        page={currentPage}
                        total={totalPages}
                        onChange={handlePageChange}
                      />
                    ) : null}
                  </div>
                }
                columns={[...LOG_COLUMNS]}
                emptyContent={
                  logsError
                    ? (logsQueryError as Error | undefined)?.message || 'Failed to load usage logs'
                    : hasAnyFilter
                      ? 'No logs match the current filter.'
                      : 'No logs yet'
                }
                enableCollapse
                isLoading={logsLoading}
                items={logs}
                loadingContent={
                  <div className="absolute top-8 flex w-full flex-col justify-start space-y-3 py-4">
                    {createLoadingSkeleton()}
                  </div>
                }
                minWidthPx={1200}
                renderCell={renderCell}
                renderCollapseContent={renderCollapseContent}
                sortDescriptor={sortDescriptor}
                onSortChange={handleSortChange}
              />
            </CardBody>
          </Card>
        </div>
      </PermissionGuard>
    </DefaultLayout>
  );
}
